// A per-path subscription store for live samples — the gauges' lane.
//
// Why this exists alongside `useBoatLiveData`'s `latest` map: a cloned-Map
// snapshot per frame re-renders every consumer of that map, which on the
// dashboard is the whole page. A wind needle does not need the alarms list to
// re-render every time a sample lands.
//
// So the WS handler feeds BOTH lanes. This one is per-path and synchronous:
// a widget subscribes to exactly the path it draws and is woken only when that
// path changes. The snapshot map keeps working as it always did, throttled to
// ≤1 Hz for its own consumers.
//
// `useSyncExternalStore` is the right primitive for an external mutable source
// like this — it handles tearing under concurrent rendering, which a
// useEffect+useState pair does not. Precedent in this codebase:
// `useOverviewDensity.ts`.

import { useCallback, useSyncExternalStore } from 'react';

export interface LiveSampleValue {
  /** The reading. */
  value: number | string | boolean | Record<string, unknown> | undefined;
  /** Local DISPLAY time (ms) — already re-timed from boat clock to this
   *  browser's, so it can be compared with `Date.now()`. */
  at: number;
  unit?: string;
  source?: string;
  deviceId?: string;
}

type Listener = () => void;

export class LiveSampleStore {
  // boatId → path → value
  private readonly byBoat = new Map<string, Map<string, LiveSampleValue>>();
  // boatId → path → listeners
  private readonly pathListeners = new Map<string, Map<string, Set<Listener>>>();
  // boatId → listeners that want ANY change on that boat
  private readonly boatListeners = new Map<string, Set<Listener>>();

  get(boatId: string, path: string): LiveSampleValue | undefined {
    return this.byBoat.get(boatId)?.get(path);
  }

  /** Subscribe to one path. Returns an unsubscribe function. */
  subscribe(boatId: string, path: string, listener: Listener): () => void {
    let forBoat = this.pathListeners.get(boatId);
    if (forBoat === undefined) {
      forBoat = new Map();
      this.pathListeners.set(boatId, forBoat);
    }
    let set = forBoat.get(path);
    if (set === undefined) {
      set = new Set();
      forBoat.set(path, set);
    }
    set.add(listener);
    return () => {
      set.delete(listener);
      // Prune empty buckets: a dashboard that swaps panes all session would
      // otherwise accumulate one empty Set per path it ever showed.
      if (set.size === 0) forBoat.delete(path);
      if (forBoat.size === 0) this.pathListeners.delete(boatId);
    };
  }

  /** Subscribe to ANY change on a boat (for aggregate views). */
  subscribeBoat(boatId: string, listener: Listener): () => void {
    let set = this.boatListeners.get(boatId);
    if (set === undefined) {
      set = new Set();
      this.boatListeners.set(boatId, set);
    }
    set.add(listener);
    return () => {
      set.delete(listener);
      if (set.size === 0) this.boatListeners.delete(boatId);
    };
  }

  /** Ingest one frame's worth of samples and wake only what changed. */
  ingest(boatId: string, samples: ReadonlyArray<{ path: string } & LiveSampleValue>): void {
    if (samples.length === 0) return;
    let forBoat = this.byBoat.get(boatId);
    if (forBoat === undefined) {
      forBoat = new Map();
      this.byBoat.set(boatId, forBoat);
    }
    const listeners = this.pathListeners.get(boatId);
    // Collect first, notify after: a listener that re-enters `ingest` (or
    // unsubscribes a sibling) must not mutate the set we are iterating.
    const woken = new Set<Listener>();
    for (const s of samples) {
      const prev = forBoat.get(s.path);
      // Same value at the same instant is a no-op — a duplicate frame must
      // not wake a needle that has nothing new to draw.
      if (prev !== undefined && prev.at === s.at && prev.value === s.value) continue;
      forBoat.set(s.path, {
        value: s.value,
        at: s.at,
        ...(s.unit !== undefined ? { unit: s.unit } : {}),
        ...(s.source !== undefined ? { source: s.source } : {}),
        ...(s.deviceId !== undefined ? { deviceId: s.deviceId } : {}),
      });
      const forPath = listeners?.get(s.path);
      if (forPath !== undefined) for (const l of forPath) woken.add(l);
    }
    if (woken.size === 0 && this.boatListeners.get(boatId) === undefined) return;
    for (const l of woken) l();
    const boatWide = this.boatListeners.get(boatId);
    if (boatWide !== undefined) for (const l of boatWide) l();
  }

  /** Drop everything for a boat (disconnect, boat switch). */
  clearBoat(boatId: string): void {
    if (this.byBoat.delete(boatId)) {
      const listeners = this.pathListeners.get(boatId);
      if (listeners !== undefined) for (const set of listeners.values()) for (const l of set) l();
      const boatWide = this.boatListeners.get(boatId);
      if (boatWide !== undefined) for (const l of boatWide) l();
    }
  }
}

/**
 * Read one live path. Re-renders ONLY when that path changes.
 *
 * Returns the same object identity between changes, which is what keeps
 * `useSyncExternalStore` from looping — it compares snapshots by identity.
 */
export function useLiveSample(
  store: LiveSampleStore,
  boatId: string | null,
  path: string | null
): LiveSampleValue | undefined {
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (boatId === null || path === null) return () => {};
      return store.subscribe(boatId, path, onChange);
    },
    [store, boatId, path]
  );
  const getSnapshot = useCallback(
    () => (boatId === null || path === null ? undefined : store.get(boatId, path)),
    [store, boatId, path]
  );
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
